/* TreatmentTab — doctor-side post-fitment dashboard for a single case.
 *
 * Lives as the "Treatment" tab on /doctor/cases/{id}. Surfaces only when
 * the case has reached `in_treatment` (Aligners Received) or later
 * (`treatment_started`, `refinement_requested`, `completed`).
 *
 * Two visual states:
 *   1. Pre-fitment (status = in_treatment, "Aligners Received")
 *      → big Log Fitment CTA + clinical context.
 *
 *   2. Post-fitment (status = treatment_started)
 *      → fitted-on date, current tray of total, day-of-tray, next-switch
 *      ETA, ± advance controls, refinement trigger.
 *
 * Why a dedicated tab (and not banners on other tabs): the post-fitment
 * lifecycle is its own distinct phase. Bunching its controls into the
 * Plan or Submission tab clutters the case header and makes the next
 * clinical action invisible. A dedicated tab puts every action in one
 * place — "what do I do next for this patient?" lives here.
 */
import React, { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
    Tooth, Calendar as CalendarIcon, CheckCircle,
    Plus, Minus, WarningCircle, Sparkle, FilePdf, Image as ImageIcon, X,
} from "@phosphor-icons/react";
import MarkFittedDialog from "@/components/case/MarkFittedDialog";
import CareCardDialog from "@/components/case/CareCardDialog";
import { LinkedCasesPanel } from "@/components/case/LinkedCasesPanel";
import TreatmentDashboard from "@/components/case/TreatmentDashboard";


/* Tone helpers — kept inline since we use them in 4 places below. */
const TONE = {
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    sky: "bg-sky-50 border-sky-200 text-sky-900",
};


function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
    } catch {
        return "—";
    }
}


function daysBetween(fromIso, toIso) {
    if (!fromIso) return null;
    const from = new Date(fromIso);
    const to = toIso ? new Date(toIso) : new Date();
    const ms = to.getTime() - from.getTime();
    return Math.max(0, Math.floor(ms / 86400000));
}


export default function TreatmentTab({ caseData, onReload }) {
    const c = caseData;
    const [fittedOpen, setFittedOpen] = useState(false);
    const [advancing, setAdvancing] = useState(false);

    const status = c.status;
    const stage = Number(c.current_aligner_stage || 0);
    const total = Number(c.total_aligners || c.aligner_count || 0);
    const cadence = Number(c.aligner_cadence_days || 7);
    const fittedAt = c.fitted_at || null;
    const stageAnchor = c.aligner_stage_updated_at || null;

    const dayOfTray = stageAnchor ? daysBetween(stageAnchor) + 1 : null;
    const nextSwitchInDays = dayOfTray ? Math.max(0, cadence - dayOfTray + 1) : null;

    const setStage = async (next) => {
        const safe = Math.max(0, Math.min(total || 99, next));
        setAdvancing(true);
        try {
            await api.patch(`/cases/${c.id}/aligner-stage`, { current_aligner_stage: safe });
            toast.success(`Stage updated to ${safe}`);
            onReload?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't update stage");
        } finally {
            setAdvancing(false);
        }
    };

    /* ─── State 1: Awaiting fitting ───────────────────────────────────
       iter83 — accept both the new `aligners_received` status (added
       in iter68 status split) AND the legacy `in_treatment` status so
       any cases that didn't get migrated still render the fitment UI
       correctly. */
    if (status === "aligners_received" || status === "in_treatment") {
        return (
            <div className="space-y-5" data-testid="treatment-tab">
                <div className={`rounded-2xl border p-5 ${TONE.amber}`}>
                    <div className="flex items-start gap-4 flex-wrap">
                        <div className="w-12 h-12 rounded-full bg-amber-100 grid place-items-center shrink-0">
                            <Tooth size={22} weight="duotone" className="text-amber-700" />
                        </div>
                        <div className="flex-1 min-w-[200px]">
                            <div className="font-display text-xl font-semibold leading-tight">
                                Aligners received — awaiting fitting
                            </div>
                            <p className="text-sm mt-1.5 leading-relaxed opacity-90">
                                The case has arrived at your clinic. Once the patient comes in and you've fitted
                                their first aligner, log it here. <strong>That date becomes Day 1</strong> of their
                                treatment — the dashboard, day-of-tray counter, and next-switch ETA all anchor
                                from it.
                            </p>
                        </div>
                        <Button
                            onClick={() => setFittedOpen(true)}
                            className="rounded-full h-11 px-6 shrink-0 bg-amber-600 hover:bg-amber-700"
                            data-testid="treatment-log-fitment-btn"
                        >
                            <Tooth size={15} weight="bold" className="mr-1.5" />
                            Log Fitment
                        </Button>
                    </div>
                </div>

                <FittingChecklistCard />

                <DownloadCareCardButton caseId={c.id} patientName={c.patient_name} />

                <PatientPhotosViewer caseId={c.id} />

                <MarkFittedDialog
                    open={fittedOpen}
                    onOpenChange={setFittedOpen}
                    caseId={c.id}
                    patientName={c.patient_name}
                    onSuccess={onReload}
                />
            </div>
        );
    }

    /* ─── State 2: Treatment in progress ──────────────────────────────
       iter93 — redesigned. Delegates the full post-fitment dashboard to
       TreatmentDashboard, which implements the 5-section layout proposed
       in the iter93 mockup (flags → aligner progress → quick actions →
       progress photos → visit log + compliance). The legacy stat layout
       is preserved as a fallback only when TreatmentDashboard fails to
       mount for any reason. */
    return <TreatmentDashboard caseData={c} onReload={onReload} />;
}


function Stat({ label, value }) {
    return (
        <div className="rounded-xl bg-secondary/40 p-3">
            <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                {label}
            </div>
            <div className="font-semibold text-sm mt-1 tabular-nums">{value}</div>
        </div>
    );
}


/** Static "what to cover during fitment" prompt for the doctor. Helps the
 * clinic give a consistent first-visit experience without needing to read
 * a separate care card. Read-only — no interactive state. */
function FittingChecklistCard() {
    const ITEMS = [
        ["Show how to seat the aligners gently using fingers, not bite force.",
            "Most patients try to bite them in — they cause pain when forced."],
        ["Walk through the daily routine: 22 hours/day, off only for meals and brushing.",
            "Set the expectation up front — the #1 reason for slipped treatment is under-wear."],
        ["Cover cleaning: cool water, soft brush, no toothpaste, weekly aligner-cleaner soak.",
            "Hot water warps the plastic; toothpaste scratches it."],
        ["Hand them the case + remind: always case, never napkins.",
            "The #1 cause of replacement charges is napkin loss."],
        ["Explain when to switch trays — point to their dashboard for the date.",
            "Best done at night so initial pressure happens during sleep."],
        ["Tell them how to reach you — and what counts as 'message us right away'.",
            "Broken aligner, sharp edge, persistent pain >3 days, lost tray."],
    ];
    return (
        <div className="rounded-2xl border border-border bg-card p-5">
            <div className="flex items-center gap-2 mb-3">
                <WarningCircle size={16} weight="duotone" className="text-primary" />
                <h3 className="font-display font-semibold text-base">Cover during the fitting visit</h3>
            </div>
            <ol className="space-y-3">
                {ITEMS.map((item, i) => (
                    <li key={i} className="flex gap-3 text-sm">
                        <span className="w-6 h-6 rounded-full bg-primary/10 text-primary font-bold text-xs grid place-items-center shrink-0">
                            {i + 1}
                        </span>
                        <div className="flex-1 leading-relaxed">
                            <div className="font-semibold text-foreground">{item[0]}</div>
                            <div className="text-xs text-muted-foreground mt-0.5">{item[1]}</div>
                        </div>
                    </li>
                ))}
            </ol>
        </div>
    );
}



/* "Customize & download care card" — opens a dialog where the doctor adds
   patient-specific notes (clinical, diet, pain, emergency) before generating
   the PDF. The dialog saves to `case.care_card` first, then downloads the
   PDF — so the saved notes survive across re-opens. iter71. */
function DownloadCareCardButton({ caseId, patientName }) {
    const [open, setOpen] = useState(false);
    return (
        <>
            <div className="rounded-2xl border border-border bg-card p-5 flex items-start gap-3" data-testid="treatment-care-card-card">
                <div className="w-10 h-10 rounded-full bg-rose-50 grid place-items-center shrink-0">
                    <FilePdf size={18} weight="duotone" className="text-rose-600" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold text-base">Patient care card</div>
                    <p className="text-sm text-muted-foreground mt-0.5 leading-relaxed">
                        Personalized take-home PDF. Auto-includes a tray switch schedule + the wearing
                        guide. Add patient-specific notes (clinical, diet, pain management, emergency
                        instructions) before downloading.
                    </p>
                </div>
                <Button
                    onClick={() => setOpen(true)}
                    variant="outline"
                    className="rounded-full shrink-0"
                    data-testid="treatment-download-care-card-btn"
                >
                    <FilePdf size={13} weight="bold" className="mr-1" />
                    Customize &amp; download
                </Button>
            </div>
            <CareCardDialog
                open={open}
                onOpenChange={setOpen}
                caseId={caseId}
                patientName={patientName}
            />
        </>
    );
}


/* Read-only doctor view of the patient's weekly progress gallery. Mounts
   in the Treatment tab so the doctor sees the patient's actual smile-in-
   progress alongside their wear-time + stage data — full clinical context
   in one screen. */
function PatientPhotosViewer({ caseId }) {
    const [photos, setPhotos] = useState(null);
    const [open, setOpen] = useState(null); // selected photo for full-screen view
    useEffect(() => {
        api.get(`/cases/${caseId}/patient-photos`)
            .then((r) => setPhotos(r.data || []))
            .catch(() => setPhotos([]));
    }, [caseId]);

    if (photos === null) return null;

    return (
        <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="treatment-patient-photos">
            <div className="px-5 py-3 border-b border-border bg-secondary/30 flex items-center gap-2">
                <ImageIcon size={14} weight="duotone" className="text-primary" />
                <div className="font-display font-semibold text-sm flex-1">
                    Patient progress photos {photos.length > 0 ? <span className="text-muted-foreground font-normal">· {photos.length}</span> : null}
                </div>
            </div>
            {photos.length === 0 ? (
                <div className="px-5 py-6 text-center text-xs text-muted-foreground">
                    Your patient hasn't uploaded any photos yet.
                </div>
            ) : (
                <div className="p-4 grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
                    {photos.map((p) => (
                        <button
                            key={p.id}
                            onClick={() => setOpen(p)}
                            className="aspect-square rounded-lg overflow-hidden border border-border group"
                            data-testid={`treatment-photo-${p.id}`}
                            title={p.note || ""}
                        >
                            <img src={p.data_url} alt={p.note || "Patient photo"} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                        </button>
                    ))}
                </div>
            )}
            {open && (
                <div
                    className="fixed inset-0 z-50 bg-black/85 grid place-items-center p-4"
                    onClick={() => setOpen(null)}
                >
                    <button
                        onClick={() => setOpen(null)}
                        className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center"
                        aria-label="Close"
                    >
                        <X size={18} weight="bold" />
                    </button>
                    <div className="max-w-3xl">
                        <img src={open.data_url} alt={open.note || "Photo"} className="max-w-full max-h-[80vh] object-contain rounded-xl" />
                        {open.note && <div className="mt-3 text-white/90 text-center text-sm">{open.note}</div>}
                        <div className="mt-1 text-white/60 text-center text-xs">
                            {new Date(open.uploaded_at).toLocaleString("en-IN")}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
